import { useTranslation } from "react-i18next";
import { CertificationType, ConsultantTagType, TagType } from "@/models/Organisation";
import { tagHoursLeft } from "@/utils/credential.utils";
import CertificationChip from "./CertificationChip";
import TagChip from "./TagChip";

/** A held tag on an entity (consultant/customer/job) — references a tag type by key. */
interface HeldTag {
	key: string;
	expiresAt?: string | null;
}

/** A held certification on a consultant — references a certification type by typeKey. */
interface HeldCertification {
	typeKey: string;
	expiresAt?: string | null;
}

interface ListViewTagChipsProps {
	/** Tags held by the entity; resolved against `tagTypes` by key. */
	tags?: HeldTag[];
	/** The org taxonomy for this list (consultant/customer/job tag types). */
	tagTypes?: (ConsultantTagType | TagType)[];
	/** Certifications held by the consultant; resolved against `certificationTypes`. */
	certifications?: HeldCertification[];
	certificationTypes?: CertificationType[];
	/** Chips shown before collapsing the rest into a "+N" pill. */
	maxVisible?: number;
}

interface ResolvedChip {
	name: string;
	color?: string;
	isCertification?: boolean;
	/** Hover text — name plus hours left for a timed tag. */
	title: string;
}

const isExpired = (expiresAt?: string | null) =>
	!!expiresAt && new Date(expiresAt).getTime() < Date.now();

/** True when any type in the org taxonomies is opted in to list views. */
export const hasListViewTypes = (...taxonomies: ({ showInListViews?: boolean }[] | undefined)[]) =>
	taxonomies.some((types) => (types ?? []).some((type) => type.showInListViews));

/**
 * Compact single-line row of tag/certification chips for list views. Renders
 * ONLY types opted in via `showInListViews` (org settings) and skips expired
 * entries, so lists stay clean by default. At most `maxVisible` chips are
 * shown; the rest collapse into a "+N" pill whose native tooltip lists them.
 * Certifications render as outlined chips with an award icon to stand apart
 * from the solid colored tag chips.
 */
const ListViewTagChips = ({
	tags,
	tagTypes,
	certifications,
	certificationTypes,
	maxVisible = 3,
}: ListViewTagChipsProps) => {
	const { t } = useTranslation();
	const chips: ResolvedChip[] = [];

	for (const held of tags ?? []) {
		if (isExpired(held.expiresAt)) continue;
		const type = tagTypes?.find((tagType) => tagType.showInListViews && tagType.key === held.key);
		if (!type) continue;
		const hoursLeft = tagHoursLeft(held.expiresAt);
		const title =
			hoursLeft !== null
				? `${type.name.sv} · ${t("consultantTag.hoursLeft", { count: hoursLeft })}`
				: type.name.sv;
		chips.push({ name: type.name.sv, color: type.color, title });
	}

	for (const held of certifications ?? []) {
		if (isExpired(held.expiresAt)) continue;
		const type = certificationTypes?.find(
			(certType) => certType.showInListViews && certType.key === held.typeKey
		);
		if (type) chips.push({ name: type.name.sv, isCertification: true, title: type.name.sv });
	}

	if (!chips.length) return null;

	const visible = chips.slice(0, maxVisible);
	const hidden = chips.slice(maxVisible);

	return (
		<div className="flex items-center gap-1 overflow-hidden">
			{visible.map((chip, i) =>
				chip.isCertification ? (
					<CertificationChip
						key={i}
						name={chip.name}
						size="sm"
						title={chip.title}
						className="max-w-[120px] shrink"
					/>
				) : (
					<TagChip
						key={i}
						name={chip.name}
						color={chip.color}
						size="sm"
						title={chip.title}
						className="max-w-[120px] shrink"
					/>
				)
			)}
			{hidden.length > 0 && (
				<span
					title={hidden.map((chip) => chip.title).join(", ")}
					className="inline-flex shrink-0 items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium leading-4 text-gray-600"
				>
					+{hidden.length}
				</span>
			)}
		</div>
	);
};

export default ListViewTagChips;
